import { useEffect, useState } from "react";
import { api } from "../api";
import { PageSection } from "../components/reporting";
import { useSession } from "../session";

const inputCls =
  "w-full h-9 px-3 rounded-xs border border-default bg-surface-white text-body text-primary placeholder:text-tertiary focus:outline-none focus:border-brand-red/50 transition-colors";

const retentionOptions = [
  { value: 90 as const, label: "90 days", description: "Shortest available retention" },
  { value: 180 as const, label: "180 days", description: "Six-month retention" },
  { value: 365 as const, label: "365 days", description: "One-year retention" },
];

export function Settings() {
  const session = useSession();
  const owner = session.role === "owner";
  const [retentionDays, setRetentionDays] = useState<90 | 180 | 365>(365);
  const [reminderOneHours, setReminderOneHours] = useState(24);
  const [reminderTwoHours, setReminderTwoHours] = useState(72);
  const [retentionMenuOpen, setRetentionMenuOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    const settings = await api<{
      retentionDays: 90 | 180 | 365;
      reminderOneHours: number;
      reminderTwoHours: number;
    }>("/api/settings");
    setRetentionDays(settings.retentionDays);
    setReminderOneHours(settings.reminderOneHours);
    setReminderTwoHours(settings.reminderTwoHours);
  };
  useEffect(() => {
    void load().catch((problem) =>
      setError(problem instanceof Error ? problem.message : "Settings unavailable"),
    );
  }, [owner, session.adminTenantAccess]);
  const remindersValid =
    Number.isInteger(reminderOneHours) &&
    Number.isInteger(reminderTwoHours) &&
    reminderOneHours >= 1 &&
    reminderOneHours <= 720 &&
    reminderTwoHours >= 1 &&
    reminderTwoHours <= 720 &&
    reminderTwoHours > reminderOneHours;

  return (
    <div className="flex flex-col gap-[24px]">
      <PageSection title="Workspace">
        <div className="grid grid-cols-2 gap-4 max-w-[620px]">
          <label className="flex flex-col gap-1.5">
            <span className="text-caption font-medium text-primary">Account name</span>
            <input className={inputCls} value={session.brand} readOnly />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-caption font-medium text-primary">Signed-in user</span>
            <input className={inputCls} value={session.userName} readOnly />
          </label>
        </div>
      </PageSection>

      <PageSection title="Lead retention">
        <div className="max-w-[620px] flex items-end gap-3">
          <div className="flex-1 flex flex-col gap-1.5">
            <label htmlFor="retention-menu" className="text-caption font-medium text-primary">
              Delete leads after
            </label>
            <div className="relative">
              <button
                id="retention-menu"
                type="button"
                aria-haspopup="listbox"
                aria-expanded={retentionMenuOpen}
                disabled={!owner}
                onClick={() => setRetentionMenuOpen((open) => !open)}
                className="w-full h-9 px-3 rounded-xs border border-default bg-surface-white text-left text-body text-primary focus:outline-none focus:border-brand-red/50 transition-colors flex items-center justify-between gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span>{retentionDays} days</span>
                <i
                  className={`fa-solid fa-chevron-down text-[11px] text-tertiary transition-transform ${retentionMenuOpen ? "rotate-180" : ""}`}
                />
              </button>
              {retentionMenuOpen && owner && (
                <div
                  role="listbox"
                  aria-label="Lead retention period"
                  className="absolute z-30 left-0 right-0 top-[calc(100%+6px)] overflow-hidden rounded-md border border-default bg-surface-white shadow-[var(--shadow-md)]"
                >
                  {retentionOptions.map((option) => {
                    const selected = option.value === retentionDays;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => {
                          setRetentionDays(option.value);
                          setRetentionMenuOpen(false);
                        }}
                        className={`w-full px-3.5 py-3 text-left border-b border-default last:border-b-0 transition-colors flex items-center justify-between gap-4 ${
                          selected ? "bg-brand-red-tint" : "hover:bg-surface-light"
                        }`}
                      >
                        <span>
                          <span className="block text-body font-medium text-primary">
                            {option.label}
                          </span>
                          <span className="block text-micro text-tertiary mt-0.5">
                            {option.description}
                          </span>
                        </span>
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                            selected ? "border-brand-red bg-brand-red" : "border-default"
                          }`}
                        >
                          {selected && <i className="fa-solid fa-check text-[9px] text-white" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() =>
              void api("/api/settings/retention", {
                method: "PUT",
                body: JSON.stringify({ retentionDays }),
              })
                .then(() => setMessage("Retention policy saved."))
                .catch((problem) =>
                  setError(problem instanceof Error ? problem.message : "Save failed"),
                )
            }
            disabled={!owner}
            className="h-9 px-4 rounded-xs bg-brand-red text-on-brand text-body font-semibold disabled:opacity-40"
          >
            Save policy
          </button>
        </div>
        <p className="text-caption text-tertiary mt-3 max-w-[620px]">
          Leads older than your selected period will be permanently deleted, along with their notes
          and activity. This helps keep your workspace up to date and limits how long personal
          information is stored.
        </p>
      </PageSection>

      <PageSection title="Lead reminders">
        <div className="max-w-[620px]">
          <p className="text-caption text-secondary mb-4">
            Email the configured sales destination when a new lead has not been opened by anyone in
            your organisation. Opening the lead cancels every remaining reminder.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-caption font-medium text-primary">Reminder 1</span>
              <span className="relative">
                <input
                  type="number"
                  min={1}
                  max={720}
                  step={1}
                  disabled={!owner}
                  value={reminderOneHours}
                  onChange={(event) => setReminderOneHours(Number(event.target.value))}
                  className={`${inputCls} pr-16 disabled:opacity-60 disabled:cursor-not-allowed`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-caption text-tertiary pointer-events-none">
                  hours
                </span>
              </span>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-caption font-medium text-primary">Reminder 2</span>
              <span className="relative">
                <input
                  type="number"
                  min={1}
                  max={720}
                  step={1}
                  disabled={!owner}
                  value={reminderTwoHours}
                  onChange={(event) => setReminderTwoHours(Number(event.target.value))}
                  className={`${inputCls} pr-16 disabled:opacity-60 disabled:cursor-not-allowed`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-caption text-tertiary pointer-events-none">
                  hours
                </span>
              </span>
            </label>
          </div>
          {!remindersValid && (
            <p className="mt-2 text-caption text-status-red">
              Use whole hours from 1 to 720, with Reminder 2 later than Reminder 1.
            </p>
          )}
          <button
            type="button"
            onClick={() => {
              setMessage("");
              setError("");
              void api("/api/settings/reminders", {
                method: "PUT",
                body: JSON.stringify({ reminderOneHours, reminderTwoHours }),
              })
                .then(() => setMessage("Lead reminder timings saved."))
                .catch((problem) =>
                  setError(problem instanceof Error ? problem.message : "Save failed"),
                );
            }}
            disabled={!owner || !remindersValid}
            className="mt-4 h-9 px-4 rounded-xs bg-brand-red text-on-brand text-body font-semibold disabled:opacity-40"
          >
            Save reminders
          </button>
        </div>
      </PageSection>

      {message && <p className="text-caption text-status-green">{message}</p>}
      {error && <p className="text-caption text-status-red">{error}</p>}
    </div>
  );
}
